// L'avatar del pop che ha scritto un messaggio, dal nome dell'autore («PM», «Sviluppatore 1»): il
// profilo con quel nome, o con il nome senza il numero dell'agente. I profili si leggono una volta
// per pagina e si tengono in memoria.
import { useEffect, useState } from 'react';
import { api } from './api';
import type { AgentProfile } from './api';

let cached: Promise<AgentProfile[]> | null = null;

/** I pop configurati, letti una volta per pagina. */
export function usePopProfiles(): AgentProfile[] {
  const [profiles, setProfiles] = useState<AgentProfile[]>([]);
  useEffect(() => {
    cached ??= api.profiles().catch(() => {
      cached = null;
      return [];
    });
    void cached.then(setProfiles);
  }, []);
  return profiles;
}

export function usePopAvatars(): (author: string | null | undefined) => string | null {
  const profiles = usePopProfiles();
  return (author) => {
    if (!author) return null;
    const name = author.trim().toLowerCase();
    const base = name.replace(/\s+\d+$/, '');
    const found = profiles.find(
      (p) => p.label.toLowerCase() === name || p.label.toLowerCase() === base,
    );
    return found?.avatar ?? null;
  };
}

/** Come si presenta chi scrive (2026-11-02, «il PM non ha nome… va usato nome [RUOLO]»): il nome
 * del pop e il suo ruolo, «Pia · PM». L'autore può essere il ruolo («PM», «Sviluppatore 1») o già
 * il nome del membro («Teo»); `role` aiuta quando il nome non basta (il ruolo del lavoro). */
export function popName(
  author: string,
  profiles: AgentProfile[],
  role?: string | null,
): { name: string; role: string | null } {
  const name = author.trim().toLowerCase();
  const base = name.replace(/\s+\d+$/, '');
  const byLabel = profiles.find(
    (p) => p.label.toLowerCase() === name || p.label.toLowerCase() === base,
  );
  if (byLabel) return { name: byLabel.nickname || author, role: byLabel.nickname ? author : null };
  const byNick = profiles.find((p) => (p.nickname ?? '').toLowerCase() === name);
  if (byNick) return { name: author, role: byNick.label };
  const byRole = role ? profiles.find((p) => p.id === role) : undefined;
  return { name: author, role: byRole?.label ?? null };
}

/** «Pia · PM», o il solo nome quando il ruolo non si conosce. */
export function popTitle(author: string, profiles: AgentProfile[], role?: string | null): string {
  const n = popName(author, profiles, role);
  return n.role && n.role !== n.name ? `${n.name} · ${n.role}` : n.name;
}
